import { useMemo, useState, useEffect, useRef } from 'react';

// Perchas por página. Con el ancho típico de una tablet son ~4 filas: entra sin
// empujar el botón de cobrar fuera de la pantalla, que es lo que pasaba con 200
// perchas en una sola grilla.
const POR_PAGINA = 50;

// Grilla de perchas del guardarropa.
// El punto del rediseño: el número lo ELIGE el operador acá, y queda reservado por la
// venta. Imprimir es una acción aparte y repetible, así que quedarse sin papel ya no
// genera "perchas fantasma" (ocupadas en el sistema, vacías en el perchero).
//
// Trabaja SIEMPRE contra `ocupadas`, que el TPV mantiene en memoria y en disco: si se
// cae la red, el guardarropa sigue sabiendo qué perchas tienen prenda.
export default function HangerGrid({ from, to, ocupadas, onPick, onOccupied, pendingLocal = [] }) {
  const ocupadasMap = useMemo(() => {
    const m = new Map();
    for (const o of (ocupadas || [])) m.set(Number(o.n), o);
    // Las asignadas en este dispositivo y todavía sin sincronizar cuentan como
    // ocupadas igual: si no, el operador podría entregar la misma percha dos veces
    // mientras no hay red.
    for (const p of (pendingLocal || [])) if (!m.has(Number(p.n))) m.set(Number(p.n), { ...p, local: true });
    return m;
  }, [ocupadas, pendingLocal]);

  const nums = useMemo(() => {
    const a = [];
    for (let n = from; n <= to; n++) a.push(n);
    return a;
  }, [from, to]);

  // ── Paginación ──
  const totalPags = Math.max(1, Math.ceil(nums.length / POR_PAGINA));
  const [pag, setPag] = useState(0);
  const pagClamp = Math.min(pag, totalPags - 1);
  const visibles = useMemo(
    () => nums.slice(pagClamp * POR_PAGINA, (pagClamp + 1) * POR_PAGINA),
    [nums, pagClamp]
  );

  // Salto automático SOLO si la página donde estoy se quedó sin perchas libres.
  // No salta en cada cobro: si lo hiciera, el operador perdería de vista dónde está
  // trabajando. Y nunca salta si el operador cambió de página a mano hace poco
  // (por ejemplo volvió a la 1 para devolver una prenda).
  const manualRef = useRef(0);
  useEffect(() => {
    if (Date.now() - manualRef.current < 15000) return;   // respeta la navegación manual
    const hayLibreAca = visibles.some(n => !ocupadasMap.has(n));
    if (hayLibreAca) return;
    const idx = nums.findIndex(n => !ocupadasMap.has(n));
    if (idx >= 0) {
      const destino = Math.floor(idx / POR_PAGINA);
      if (destino !== pagClamp) setPag(destino);
    }
  }, [visibles, ocupadasMap, nums, pagClamp]);

  const irA = (p) => {
    manualRef.current = Date.now();
    setPag(Math.max(0, Math.min(totalPags - 1, p)));
  };

  // Deslizar al costado para cambiar de página (natural en tablet). El umbral de
  // 60px evita que un tap con temblor cuente como swipe.
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? 0) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 60) return;
    irA(pagClamp + (dx < 0 ? 1 : -1));   // arrastrar a la izquierda = página siguiente
  };

  // Perchas fuera del rango que están ocupadas (las "extra" que se dieron con el
  // perchero lleno). Se muestran aparte para que se vean y se puedan devolver.
  const extras = useMemo(
    () => [...ocupadasMap.keys()].filter(n => n < from || n > to).sort((a, b) => a - b),
    [ocupadasMap, from, to]
  );

  const libres = nums.filter(n => !ocupadasMap.has(n)).length;
  const lleno = libres === 0;

  const Celda = ({ n, extra }) => {
    const oc = ocupadasMap.get(n);
    const on = !!oc;
    return (
      <button
        onClick={() => (on ? onOccupied(n, oc) : onPick(n, false))}
        title={on ? (oc.prenda || 'Ocupada') : 'Libre'}
        style={{
          position: 'relative',
          aspectRatio: '1',
          minHeight: 62,
          borderRadius: 6,
          cursor: 'pointer',
          border: '1.5px solid ' + (on ? 'var(--red-500)' : 'rgba(255,255,255,0.18)'),
          background: on ? 'rgba(200,16,46,0.22)' : 'var(--ink-200)',
          color: '#fff',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
          padding: 4,
        }}
      >
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{n}</span>
        {on && oc.prenda && (
          <span style={{ fontSize: 9.5, color: 'var(--ink-800)', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{oc.prenda}</span>
        )}
        {on && oc.local && <span style={{ position: 'absolute', top: 3, right: 4, fontSize: 9, color: '#d4af37' }}>●</span>}
        {extra && <span style={{ position: 'absolute', top: 3, left: 4, fontSize: 9, color: '#d4af37' }}>+</span>}
      </button>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="lb-eyebrow">Perchas {from}–{to}</div>
        <div style={{ fontSize: 12, color: lleno ? 'var(--red-400)' : 'var(--ink-700)' }}>
          {libres} libres · {ocupadasMap.size} con prenda
        </div>
        {totalPags > 1 && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
            página {pagClamp + 1} de {totalPags} · deslizá al costado para cambiar
          </div>
        )}
        {lleno && (
          <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => onPick(Math.max(to, ...ocupadasMap.keys()) + 1, true)}>
            Perchero lleno · usar percha extra →
          </button>
        )}
      </div>

      {/* Paginado: con 200 perchas, una sola grilla empujaba el botón de cobrar
          fuera de la pantalla. Se puede volver a cualquier página para devolver
          una prenda. En tablet también se cambia deslizando al costado. */}
      {totalPags > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => irA(pagClamp - 1)} disabled={pagClamp === 0}>←</button>
          {Array.from({ length: totalPags }, (_, i) => {
            const desde = from + i * POR_PAGINA;
            const hasta = Math.min(to, desde + POR_PAGINA - 1);
            const libresPag = nums.slice(i * POR_PAGINA, (i + 1) * POR_PAGINA).filter(n => !ocupadasMap.has(n)).length;
            const on = i === pagClamp;
            return (
              <button key={i} onClick={() => irA(i)} style={{
                padding: '7px 12px', borderRadius: 999, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                border: '1px solid ' + (on ? 'var(--red-500)' : 'var(--border)'),
                background: on ? 'var(--red-600, #C8102E)' : 'var(--ink-200)', color: '#fff', whiteSpace: 'nowrap',
              }}>
                {desde}–{hasta}
                <span style={{ opacity: 0.65, fontSize: 11, marginLeft: 6 }}>{libresPag === 0 ? 'lleno' : libresPag + ' libres'}</span>
              </button>
            );
          })}
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => irA(pagClamp + 1)} disabled={pagClamp === totalPags - 1}>→</button>
        </div>
      )}

      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(62px, 1fr))', gap: 8, touchAction: 'pan-y' }}
      >
        {visibles.map(n => <Celda key={n} n={n} />)}
      </div>

      {extras.length > 0 && (
        <>
          <div className="lb-eyebrow" style={{ margin: '18px 0 8px', color: '#d4af37' }}>Perchas extra (fuera del rango)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(62px, 1fr))', gap: 8 }}>
            {extras.map(n => <Celda key={n} n={n} extra />)}
          </div>
        </>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink-600)' }}>
        Tocá una percha <b style={{ color: 'var(--ink-800)' }}>libre</b> para asignarla, o una <b style={{ color: 'var(--red-400)' }}>ocupada</b> para devolver la prenda o reimprimir. El punto ámbar marca las que todavía no se sincronizaron.
      </div>
    </div>
  );
}
